import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function ArrivalsDeparturesTab({ tenantId, isActive }) {
    const [adData, setAdData] = useState([]);
    const [isLoadingAd, setIsLoadingAd] = useState(false);
    const [adError, setAdError] = useState(null);
    const [adFilters, setAdFilters] = useState({ date: '' });
    const [adPage, setAdPage] = useState(0);
    const [adRowsPerPage, setAdRowsPerPage] = useState(20);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchAdData();
        }
    }, [isActive, tenantId, adFilters, adPage, adRowsPerPage]);

    const fetchAdData = async (isExport = false) => {
        setIsLoadingAd(true);
        setAdError(null);
        try {
            const params = { ...adFilters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = adPage + 1;
                params.limit = adRowsPerPage;
            }
            
            const res = await ReportService.getArrivalsDeparturesReport(tenantId, params);
            const reportData = res?.data || res;
            
            let parsedItems = [];
            if (reportData?.arrivals || reportData?.departures) {
                const arrivalsList = (reportData.arrivals || []).map(item => ({
                    ...item,
                    type: 'Arrival',
                    timestamp: item.checkIn || item.timestamp
                }));
                const departuresList = (reportData.departures || []).map(item => ({
                    ...item,
                    type: 'Departure',
                    timestamp: item.checkOut || item.timestamp
                }));
                parsedItems = [...arrivalsList, ...departuresList];
            } else {
                parsedItems = reportData?.items || (Array.isArray(reportData) ? reportData : []);
            }
            
            setAdData(parsedItems);
        } catch (error) {
            console.error('Error fetching arrivals & departures report:', error);
            setAdError('Failed to load arrivals & departures report. Please try again.');
        } finally {
            setIsLoadingAd(false);
        }
    };

    const handleAdFilterChange = (field, value) => {
        setAdFilters(prev => ({ ...prev, [field]: value }));
        setAdPage(0);
    };

    const resetAdFilters = () => {
        setAdFilters({ date: '' });
        setAdPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setAdError(null);
        try {
            // Force the API to return CSV, which we will convert in the browser
            const params = { ...adFilters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportArrivalsDeparturesReport(tenantId, params);
            
            // Extract the raw CSV text safely
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            // Parse CSV into an array of arrays
            const rows = csvText.split('\n')
                .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
                .filter(row => row.length > 0 && row.some(cell => cell !== ''));
                
            const fileName = `Arrivals_Departures_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'csv') {
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${fileName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else if (format === 'excel') {
                const ws = XLSX.utils.aoa_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Arrivals & Departures");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const headers = rows[0] || [];
                const body = rows.slice(1) || [];
                
                doc.text("Arrivals & Departures Report", 14, 15);
                autoTable(doc, {
                    head: [headers],
                    body: body,
                    startY: 20,
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: [63, 81, 181] }
                });
                doc.save(`${fileName}.pdf`);
            }
        } catch (error) {
            console.error(`Error exporting arrivals & departures report as ${format}:`, error);
            setAdError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!adError} 
                autoHideDuration={6000} 
                onClose={() => setAdError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setAdError(null)}>{adError}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Target Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={adFilters.date}
                            onChange={(e) => handleAdFilterChange('date', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(adFilters.date !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetAdFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Guest Reference ID / Folio</TableCell>
                            <TableCell>Movement Classification</TableCell>
                            <TableCell>Assigned Room / Profile</TableCell>
                            <TableCell align="right">Target Timestamp / Window</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingAd ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            adData.length > 0 ? (
                                adData.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>{row.guestName || row.folio || row.guestReferenceId || '-'}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={row.type || row.movementClassification || 'Unknown'} 
                                                size="small" 
                                                color={(row.type || row.movementClassification || '').toLowerCase() === 'arrival' ? 'info' : ((row.type || row.movementClassification || '').toLowerCase() === 'departure' ? 'warning' : 'default')} 
                                            />
                                        </TableCell>
                                        <TableCell>{row.roomProfile || row.roomNumber || row.assignedRoom || '-'}</TableCell>
                                        <TableCell align="right">{row.timestamp || row.scheduledTime || row.targetTimestamp || '-'}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No logistical records found for the selected date.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={adData.length >= adRowsPerPage ? -1 : adData.length}
                    page={adPage}
                    onPageChange={(e, newPage) => setAdPage(newPage)}
                    rowsPerPage={adRowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setAdRowsPerPage(parseInt(e.target.value, 10));
                        setAdPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
